import { useState, useEffect } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import apiService from '../services/shared/apiService';

export interface PredictiveInsight {
  trajectory_analysis: {
    trajectory: 'positive' | 'negative' | 'mixed' | 'stable' | 'insufficient_data';
    confidence: number;
    trend_direction: 'increasing' | 'decreasing' | 'stable';
    trend_magnitude: number;
    daily_scores: Record<string, {
      score: number;
      positive_count: number;
      negative_count: number;
      neutral_count: number;
      total_tags: number;
    }>;
    positive_tags: string[];
    negative_tags: string[];
    neutral_tags: string[];
  };
  forecast: {
    forecast_text: string;
    forecast_type: 'positive_trajectory' | 'risk_decline' | 'mixed_unstable' | 'stable';
    confidence: number;
    top_tags: [string, number][];
    recommendations: string[];
  };
  tag_frequency: Record<string, number>;
  analysis_period: string;
  generated_at: string;
}

export const usePredictiveInsights = (days: number = 7) => {
  const { user } = useAuth();
  const [insights, setInsights] = useState<PredictiveInsight | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPredictiveInsights = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getPredictiveInsights(days);
      setInsights(data);
    } catch (err) {
      console.error('Error fetching predictive insights:', err);
      setError('Failed to load predictive insights');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchPredictiveInsights();
    }
  }, [user, days]);

  const getTrajectoryIcon = (trajectory: string) => {
    switch (trajectory) {
      case 'positive':
        return '📈';
      case 'negative':
        return '📉';
      case 'mixed':
        return '⚖️';
      case 'stable':
        return '➡️';
      default:
        return '❓';
    }
  };

  const getTrajectoryColor = (trajectory: string) => {
    switch (trajectory) {
      case 'positive':
        return 'text-green-600';
      case 'negative':
        return 'text-red-600';
      case 'mixed':
        return 'text-yellow-600';
      case 'stable':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTrajectoryLabel = (trajectory: string) => {
    switch (trajectory) {
      case 'positive':
        return 'Positive Trajectory';
      case 'negative':
        return 'Risk/Decline';
      case 'mixed':
        return 'Mixed/Unstable';
      case 'stable':
        return 'Stable';
      default:
        return 'Insufficient Data';
    }
  };

  return {
    insights,
    isLoading,
    error,
    fetchPredictiveInsights,
    getTrajectoryIcon,
    getTrajectoryColor,
    getTrajectoryLabel
  };
}; 